import Modal from "components/elements/Modal";
import classes from "./classes.module.scss";
import Typography from "components/elements/Typography";
import { XMarkIcon } from "@heroicons/react/24/solid";
import I18n from "../I18n";
import Form, { useForm } from "../Form";
import ProjectLeaderInputs from "./ProjectLeaderInputs";
import ProjectInfoInputs from "./ProjectInfoInputs";
import { useCallback, useState } from "react";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import { container } from "tsyringe";
import ProjectService from "services/ProjectService";
import ProjectCreateRequestResource from "common/resources/Project/ProjectCreateRequestResource";
import { EProjectFormat } from "common/enums/ProjectFormat/ProjectFormat";
import { EProjectTheme } from "common/enums/ProjectTheme/ProjectTheme";
import { ELang } from "common/enums/User/ELang";
import assert from "assert";
import ConfigService from "services/projects/ConfigService";
import ProjectConfigRequestResource from "common/resources/Project/Config/ProjectConfig/ProjectConfigRequestResource";
import { I18nTextResource } from "common/resources/I18nText/I18nTextResource";
import ProjectLeaderService from "services/projects/ProjectLeaderService";
import TeamService from "services/projects/TeamService";
import ContributorRequestResource from "common/resources/Project/Config/Contributor/ContributorRequestResource";
import { EProjectPurpose } from "common/enums/ProjectPurpose";
import { useNavigate } from "react-router-dom";
import ModuleConfig from "configs/ModuleConfig";

const projectService = container.resolve(ProjectService);
const projectConfigService = container.resolve(ConfigService);
const projectTeamService = container.resolve(TeamService);
const projectLeaderService = container.resolve(ProjectLeaderService);
const modules = container.resolve(ModuleConfig).get().modules;

type IProps = {
	isOpen: boolean;
	onClose: () => void;
	className?: string;
};

type FormData = {
	[key: string]: unknown;
};

export default function CreateProjectModal(props: IProps) {
	const navigate = useNavigate();
	const { handleError, clearErrors, errors } = useForm();
	const [isLoading, setIsLoading] = useState(false);
	const [selectedTheme, setSelectedTheme] = useState<EProjectTheme | null>(null);

	const validateProjectData = useCallback(
		(formData: FormData) => {
			assert(selectedTheme, "No selected theme");

			const projectResource = ProjectCreateRequestResource.hydrate<ProjectCreateRequestResource>({
				title: formData["title"] as string,
				pitch: formData["pitch"] as string,
				format: formData["format"] as EProjectFormat,
				theme: selectedTheme,
				purposes: [EProjectPurpose.AUTHENTICITY_PROTECTION],
				lang: I18n.getLang().toLocaleUpperCase() as ELang,
			});

			return projectResource.validateOrReject();
		},
		[selectedTheme],
	);

	const validateContributorData = useCallback((formData: FormData) => {
		const contributorResource = ContributorRequestResource.hydrate<ContributorRequestResource>({
			firstName: formData["firstName"] as string,
			lastName: formData["lastName"] as string,
			companyName: formData["companyName"] as string,
		});

		return contributorResource.validateOrReject();
	}, []);

	const validateProjectConfig = useCallback((formData: FormData) => {
		const titleResource = I18nTextResource.hydrateArray<I18nTextResource>([{ lang: I18n.getLang(), text: formData["title"] as string }]);
		const descriptionResource = I18nTextResource.hydrateArray<I18nTextResource>([{ lang: I18n.getLang(), text: formData["pitch"] as string }]);

		const projectConfigResource = ProjectConfigRequestResource.hydrate<ProjectConfigRequestResource>({
			title: titleResource,
			description: descriptionResource,
		});

		return projectConfigResource.validateOrReject();
	}, []);

	const onSubmit = useCallback(
		async (formData: FormData) => {
			try {
				clearErrors();
				setIsLoading(true);

				const projectResource = await validateProjectData(formData);
				const contributorResource = await validateContributorData(formData);
				const projectConfigResource = await validateProjectConfig(formData);

				const scenario = (formData["scenario"] as FileList)?.[0];
				const cv = (formData["cv"] as FileList)?.[0];
				const project = await projectService.createProject(projectResource, { cv, scenario });
				const team = await projectTeamService.create(project.id);

				const projectLeaderAvatar = (formData["leaderPicture"] as FileList)?.[0];
				await projectLeaderService.create(project.id, team.id, contributorResource, projectLeaderAvatar);
				await projectConfigService.create(project.id, projectConfigResource);

				const currentPath = window.location.pathname;
				navigate(modules.pages.EditProjectConfig.props.path.replace(":projectId", project.id).concat("?redirectPath=", currentPath));
			} catch (error) {
				setIsLoading(false);
				handleError(error);
			}
		},
		[clearErrors, validateProjectConfig, handleError, navigate, validateContributorData, validateProjectData],
	);

	return (
		<Modal isOpen={props.isOpen} onClose={props.onClose} className={classes["root"]}>
			<div className={classes["title-container"]}>
				<Typography type="p" size="large" weight="bold">
					{I18n.asset.component.create_project_modal.title}
				</Typography>

				<XMarkIcon className={classes["icon"]} onClick={props.onClose} />
			</div>

			<Form onSubmit={onSubmit} errors={errors} className={classes["form"]}>
				<ProjectInfoInputs onSelect={setSelectedTheme} selectedTheme={selectedTheme} />
				<ProjectLeaderInputs />
				<div className={classes["buttons-container"]}>
					<Button variant={EButtonVariant.TEXT} color={EButtonColor.PRIMARY} disabled={isLoading} onClick={props.onClose}>
						{I18n.asset.component.create_project_modal.buttons.cancel}
					</Button>
					<Button type="submit" variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} disabled={!Boolean(selectedTheme)} isLoading={isLoading}>
						{I18n.asset.component.create_project_modal.buttons.submit}
					</Button>
				</div>
			</Form>
		</Modal>
	);
}
